import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, ArrowRight, Mouse } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem, MaskedLines } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { QuoteDialog } from "@/components/QuoteDialog";
import { SERVICES } from "@/lib/data";
import { assets, CONTACT } from "@/lib/assets";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-navy-900">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={assets.heroBg} alt="" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-navy-900" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/40 to-transparent" />

      <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-emerald-brand mb-6"
        >
          <span className="h-px w-10 bg-emerald-brand" />
          Management Consulting
        </motion.p>

        <h1 className="font-heading font-extrabold text-white tracking-tighter leading-[0.95] text-6xl sm:text-7xl lg:text-8xl xl:text-9xl">
          <MaskedLines
            lines={["Accurate.", "Growth.", "Experience."]}
            lineClassName=""
          />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <p className="max-w-md text-white/70 text-lg leading-relaxed">
            Accounting services & financial advice in Aruba — a personalized approach that gives
            you peace of mind and more time for your business.
          </p>
          <a
            href={`tel:${CONTACT.phone}`}
            data-testid="hero-cta-button"
            className="group inline-flex items-center gap-3 rounded-full bg-emerald-brand px-8 py-4 text-sm font-bold uppercase tracking-widest text-navy-900 transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-emerald-light hover:shadow-[0_18px_40px_-12px_rgba(16,185,129,0.6)]"
          >
            Get in Touch
            <Phone size={16} strokeWidth={2.5} className="transition-transform duration-300 group-hover:rotate-12" />
          </a>
        </motion.div>
      </div>

      <motion.a
        href="#mission"
        data-testid="scroll-indicator"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60 hover:text-white transition-colors"
      >
        <Mouse size={22} />
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="text-[10px] uppercase tracking-[0.25em]"
        >
          Scroll
        </motion.span>
      </motion.a>
    </section>
  );
};

const Mission = () => (
  <section id="mission" data-testid="mission-section" className="relative py-24 sm:py-32 lg:py-40 bg-offwhite">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-6">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-brand">01 — Our Mission</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-heading font-bold text-navy tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
            Mission
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 text-lg sm:text-xl leading-relaxed text-slate-600 max-w-xl">
            To provide accounting services and financial advice with problem-solving skills and a
            personalized approach for the customer to get the necessary peace of mind and more
            valuable time for the business operations.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <Link
            to="/services"
            data-testid="mission-services-link"
            className="group mt-10 inline-flex items-center gap-2 text-navy font-semibold"
          >
            Explore our services
            <ArrowRight size={18} className="text-emerald-brand transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </div>

      <div className="lg:col-span-6">
        <Reveal delay={0.15} y={60}>
          <div className="relative">
            <div className="absolute -inset-6 bg-emerald-brand/5 rounded-[2rem] blur-2xl" />
            <div className="relative rounded-[2rem] bg-white border border-slate-200/70 p-10 sm:p-14 shadow-[0_30px_60px_-25px_rgba(9,30,54,0.2)]">
              <img
                src={assets.mission}
                alt="AGE-T mission — precision and momentum"
                className="w-full max-w-md mx-auto drop-shadow-xl"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section id="services" data-testid="services-preview" className="relative py-24 sm:py-32 lg:py-40 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
        <div className="max-w-2xl">
          <Reveal><p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-brand">02 — What we do</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-heading font-bold text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-6xl">
              Services built around your business
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <Link
            to="/services"
            data-testid="services-preview-cta"
            className="group inline-flex items-center gap-2 rounded-full border border-navy/15 px-6 py-3 text-sm font-semibold text-navy transition-[border-color,background-color] duration-300 hover:border-emerald-brand hover:bg-emerald-brand/5"
          >
            All services
            <ArrowUpRight size={17} className="text-emerald-brand transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>

      <StaggerGroup className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <StaggerItem key={s.slug}>
            <Link
              to={`/services#${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="group relative flex flex-col h-full rounded-2xl bg-white border border-slate-200/80 p-8 transition-[transform,box-shadow,border-color] duration-400 hover:-translate-y-2 hover:shadow-[0_30px_50px_-20px_rgba(9,30,54,0.22)] hover:border-emerald-brand/40"
            >
              <div className="mb-6 h-56 w-56 overflow-hidden">
                <img
                  src={s.icon}
                  alt=""
                  className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <span className="text-xs font-bold text-slate-300 tracking-widest">0{i + 1}</span>
              <h3 className="mt-2 font-heading font-semibold text-xl text-navy leading-snug">{s.title}</h3>
              <p className="mt-4 text-slate-500 leading-relaxed text-[15px] flex-1">{s.short}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy">
                Learn more
                <ArrowRight size={15} className="text-emerald-brand transition-transform duration-300 group-hover:translate-x-1.5" />
              </span>
            </Link>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </div>
  </section>
);

const CTABanner = () => {
  const [quoteOpen, setQuoteOpen] = useState(false);
  return (
  <section data-testid="cta-banner" className="relative overflow-hidden bg-navy-900 grain">
    <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-emerald-brand/15 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-28 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-brand mb-5">Quick & Effective</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-heading font-bold text-white tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            Accounting Services & Financial Advice
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <button
            type="button"
            onClick={() => setQuoteOpen(true)}
            data-testid="request-quote-cta"
            className="group mt-10 inline-flex items-center gap-3 rounded-full bg-emerald-brand px-8 py-4 text-sm font-bold uppercase tracking-widest text-navy-900 transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-emerald-light hover:shadow-[0_18px_40px_-12px_rgba(16,185,129,0.6)]"
          >
            Request a Quote
            <ArrowUpRight size={17} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </Reveal>
      </div>
      <div className="lg:col-span-4 flex lg:justify-end">
        <Reveal delay={0.2} y={30}>
          <img src={assets.logoWhite} alt="AGE-T" className="h-16 w-auto opacity-90" />
        </Reveal>
      </div>
    </div>
    <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
  </section>
  );
};

const FinalCTA = () => (
  <section data-testid="final-cta" className="bg-offwhite py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
      <Reveal>
        <h2 className="font-heading font-bold text-navy tracking-tight text-4xl sm:text-5xl lg:text-6xl">
          Further questions?
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <a
          href={`tel:${CONTACT.phone}`}
          data-testid="final-cta-button"
          className="group mt-10 inline-flex items-center gap-3 rounded-full bg-navy px-9 py-5 text-sm font-bold uppercase tracking-widest text-white transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-12px_rgba(9,30,54,0.5)]"
        >
          Get in Touch
          <Phone size={16} strokeWidth={2.5} className="text-emerald-brand transition-transform duration-300 group-hover:rotate-12" />
        </a>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Mission />
      <Marquee tone="light" />
      <ServicesPreview />
      <CTABanner />
      <FinalCTA />
    </>
  );
}
